import { EventBus, Events } from "../eventBus";
import { initAnchors } from "./anchors";
import { initEditorButtons } from "./buttons";
import { ElemID, PLACEHOLDER_MIN_DELAY } from "./constants";
import { maybeEnableDebugComponent } from "./debug";
import { initInteractions } from "./interactions";
import {
  loadPdf,
  pdfHasFormWidgets,
  initialPdfRender,
  renderPreview,
  pdfIsQwilSignTemplate,
} from "./pdf";
import {
  destroyPreview,
  freePreviewCanvases,
  renderAnchorMenu,
} from "./renderer";
import { initSidebar } from "./sidebar";
import { hide, unhide, waitForAtLeast } from "../../utils";
import { initWayfinder, wayfinderTrackActivePage } from "./wayfinder";

const editorComponent = document.getElementById(ElemID.EDITOR_COMPONENT);
const previewComponent = document.getElementById(ElemID.PREVIEW_COMPONENT);
const headerButtons = document.getElementById(ElemID.HEADER_BUTTON_CONTAINER);
const previewButton = document.getElementById(ElemID.PREVIEW_BUTTON);
const cancelButton = document.getElementById(ElemID.CANCEL_BUTTON);
const backButton = document.getElementById(ElemID.BACK_BUTTON);
const exportButton = document.getElementById(ElemID.EXPORT_BUTTON);

EventBus.subscribe(Events.PDF_UPLOADED, async function (payload) {
  // first, show a placeholder while we prepare the editor and render the PDF
  EventBus.emit(Events.SHOW_PLACEHOLDER_PREPARING_PDF);

  const result = await waitForAtLeast(
    PLACEHOLDER_MIN_DELAY,
    initComponent(payload),
  );

  EventBus.emit(Events.HIDE_PLACEHOLDER);
  if (result.success) {
    showComponent();
    // can only set screen to auto-fit after window is visible
    EventBus.emit(Events.ZOOM_VALUE_UPDATED, { zoom: "fit", screen: "editor" });
  } else {
    EventBus.emit(Events.PDF_REJECTED, { reason: result.reason });
  }

  maybeEnableDebugComponent();
});

EventBus.subscribe(Events.SHOW_PLACEHOLDER_GENERATING_PDF, function () {
  hideComponent();
});

let previewScreenScrollTracker;

EventBus.subscribe(Events.SHOW_PREVIEW, async function () {
  hide(previewButton);
  hide(cancelButton);
  hide(editorComponent);

  unhide(exportButton);
  unhide(backButton);
  unhide(previewComponent);

  // TODO: use same pages and just reapply anchors instead of rerender each time.
  destroyPreview(); // delete previous content if exists and start again
  await renderPreview();
  previewScreenScrollTracker = wayfinderTrackActivePage(true);
});

EventBus.subscribe(Events.HIDE_PREVIEW, function () {
  unhide(previewButton);
  unhide(cancelButton);
  unhide(editorComponent);

  hide(exportButton);
  hide(backButton);
  hide(previewComponent);

  // don't run destroyPreview yet in case rendering still happening when HIDE_PREVIEW triggered.
  freePreviewCanvases();

  if (previewScreenScrollTracker) {
    previewScreenScrollTracker.destroy();
    previewScreenScrollTracker = null;
  }
});

async function initComponent(pdfSrc) {
  const result = await loadPdf(pdfSrc);
  if (!result.success) {
    return result;
  }

  if (await pdfHasFormWidgets()) {
    return {
      success: false,
      reason: "widgets",
    };
  }

  if (await pdfIsQwilSignTemplate()) {
    return {
      success: false,
      reason: "template",
    };
  }

  await initialPdfRender();
  await initAnchors();
  renderAnchorMenu();
  initWayfinder(); // must be called after pdf is rendered
  wayfinderTrackActivePage(); // must be called after wayfinder initialised
  initInteractions();
  initEditorButtons();
  initSidebar();
  return { success: true };
}

function showComponent() {
  unhide(editorComponent);
  unhide(headerButtons);
}

function hideComponent() {
  hide(editorComponent);
  hide(headerButtons);
}
